import React, { useState, useLayoutEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * First-run coach-mark overlay for the creator.
 *
 * Props:
 *   steps: Array<{ title: string, body: string, target?: string }>
 *     - target: a value matching a `data-coach="<target>"` attribute on a
 *       mounted element. If present and found, the overlay cuts a spotlight
 *       around that element and anchors the tooltip beneath it. If absent or
 *       not found, the tooltip renders centered.
 *   onDone: () => void  — called when the last step is dismissed or Skip.
 *
 * Uses candy_heart (--color-*) vars to match the in-app theme.
 */
export default function Coachmarks({ steps = [], onDone }) {
  const { t } = useTranslation();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[i];

  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);

  useLayoutEffect(() => {
    if (!step) return undefined;
    const measure = () => {
      if (!step.target) { setRect(null); return; }
      const el = document.querySelector(`[data-coach="${step.target}"]`);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [i, steps]);

  const finish = useCallback(() => onDone && onDone(), [onDone]);
  const next = useCallback(() => {
    if (i >= steps.length - 1) finish();
    else setI((n) => n + 1);
  }, [i, steps, finish]);

  useLayoutEffect(() => {
    returnFocusRef.current = document.activeElement;
    dialogRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") finish(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const el = returnFocusRef.current;
      if (el && typeof el.focus === "function") el.focus();
    };
  }, [finish]);

  if (!step) return null;

  const last = i === steps.length - 1;
  const PAD = 8;

  const spotlight = rect
    ? {
        position: "fixed",
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
        borderRadius: 14,
        boxShadow: "0 0 0 9999px rgba(15, 8, 14, 0.66)",
        pointerEvents: "none",
        zIndex: 4000,
        transition: "top 0.25s cubic-bezier(.2,.7,.2,1), left 0.25s cubic-bezier(.2,.7,.2,1), width 0.25s cubic-bezier(.2,.7,.2,1), height 0.25s cubic-bezier(.2,.7,.2,1)",
      }
    : null;

  // Width is responsive — never wider than the viewport minus gutters (mobile).
  const TOOLTIP_W = Math.min(300, window.innerWidth - 32);
  // Reserve space for the floating bottom nav so the tooltip never collides
  // with it (the old "place directly under the target" logic overlapped the
  // nav on small screens).
  const NAV_RESERVE = 104;
  let tooltipStyle;
  if (rect) {
    const left = Math.min(
      Math.max(12, rect.left + rect.width / 2 - TOOLTIP_W / 2),
      window.innerWidth - TOOLTIP_W - 12
    );
    // Default: anchor the card to the bottom (above the nav) — stable, never
    // clipped. If the spotlighted target sits low (its highlight would be
    // hidden behind a bottom card), flip the card to the top instead.
    const targetNearBottom = rect.bottom > window.innerHeight - 240;
    tooltipStyle = targetNearBottom
      ? {
          position: "fixed",
          left,
          top: "calc(env(safe-area-inset-top, 0px) + 70px)",
          width: TOOLTIP_W,
          zIndex: 4001,
        }
      : {
          position: "fixed",
          left,
          bottom: `calc(env(safe-area-inset-bottom, 0px) + ${NAV_RESERVE}px)`,
          width: TOOLTIP_W,
          zIndex: 4001,
        };
  } else {
    tooltipStyle = {
      position: "fixed",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      width: TOOLTIP_W,
      zIndex: 4001,
    };
  }

  return (
    <div
      role="dialog"
      aria-label={t("ui.coachmarks.dialogAria")}
      aria-modal="true"
      data-testid="coachmarks"
      ref={dialogRef}
      tabIndex={-1}
      style={{ position: "fixed", inset: 0, zIndex: 3999 }}
    >
      {!rect && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 8, 14, 0.66)",
            zIndex: 4000,
          }}
        />
      )}
      {spotlight && <div style={spotlight} />}

      <div
        style={{
          ...tooltipStyle,
          background: "var(--color-surface, #fff)",
          borderRadius: 20,
          padding: "18px 18px 16px",
          boxShadow: "0 18px 50px -12px rgba(0,0,0,0.5)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--color-primary)",
            marginBottom: 6,
          }}
        >
          {t("ui.coachmarks.stepCounter", { current: i + 1, total: steps.length })}
        </div>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            fontSize: 19,
            letterSpacing: "-0.01em",
            color: "var(--color-text)",
            marginBottom: 6,
          }}
        >
          {step.title}
        </div>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 14,
            lineHeight: 1.45,
            color: "var(--color-textMuted)",
            margin: "0 0 16px",
          }}
        >
          {step.body}
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <button
            type="button"
            onClick={finish}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--color-textDim)",
              padding: "6px 4px",
            }}
          >
            {t("ui.coachmarks.skip")}
          </button>
          <button
            type="button"
            onClick={next}
            style={{
              background: "var(--color-primary)",
              color: "var(--color-onPrimary, #fff)",
              border: "none",
              borderRadius: 999,
              cursor: "pointer",
              fontFamily: "var(--font-display)",
              fontWeight: 800,
              fontSize: 14,
              padding: "10px 22px",
            }}
          >
            {last ? t("ui.coachmarks.done") : t("ui.coachmarks.next")}
          </button>
        </div>
      </div>
    </div>
  );
}
